import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, CreditCard, Truck, User } from "lucide-react";
import api, { formatCAD, formatApiErrorDetail, SQUARE_ENABLED } from "@/lib/api";
import { getPickupSlots } from "@/lib/assets";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/logger";
import { InformationStep, ShippingStep, PaymentStep, OrderSummary } from "@/components/CheckoutSteps";

const STEPS = [
  { id: 0, label: "Information", icon: User },
  { id: 1, label: "Shipping", icon: Truck },
  { id: 2, label: "Payment", icon: CreditCard },
];

const PICKUP_RATE = { id: "pickup", name: "In-store Pickup (Calgary)", eta: "You'll get an email or SMS when it's ready", amount_cents: 0, carrier: "Bambam African Mart" };

export default function Checkout() {
  const { items, subtotalCents, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [method, setMethod] = useState("ship"); // "ship" | "pickup"
  const [pickupSlot, setPickupSlot] = useState("");
  const pickupSlots = useMemo(() => getPickupSlots(), []);
  const [placing, setPlacing] = useState(false);
  const [info, setInfo] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    line1: "", line2: "", city: "", province: "AB", postal_code: "", country: "CA",
  });
  const [rates, setRates] = useState([]);
  const [rateId, setRateId] = useState("");
  const [loadingRates, setLoadingRates] = useState(false);
  const [deliveryError, setDeliveryError] = useState("");
  const [quoteInfo, setQuoteInfo] = useState(null);
  const [card, setCard] = useState({ number: "", exp: "", cvc: "", name: "" });
  const [squareCfg, setSquareCfg] = useState({ configured: false });
  const squareRef = useRef(null);
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(null); // {code, discount_cents}
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    if (user) setInfo((s) => ({ ...s, customer_name: s.customer_name || user.name, customer_email: s.customer_email || user.email }));
  }, [user]);

  useEffect(() => {
    api.get("/square/status").then((r) => setSquareCfg(r.data)).catch((e) => logError("Square status fetch failed", e));
  }, []);

  useEffect(() => {
    if (items.length === 0 && !placing) navigate("/cart");
  }, [items, placing, navigate]);

  const selectedRate = useMemo(() => rates.find((r) => r.id === rateId), [rates, rateId]);
  const shippingCents = selectedRate ? selectedRate.amount_cents : 0;
  const discountCents = discount ? discount.discount_cents : 0;
  const taxableCents = Math.max(0, subtotalCents - discountCents);
  const taxCents = Math.round(taxableCents * 0.05);
  const totalCents = taxableCents + shippingCents + taxCents;

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: promo.trim(), subtotal_cents: subtotalCents });
      if (data.valid) {
        setDiscount({ code: data.code, discount_cents: data.discount_cents });
        toast.success(`Code ${data.code} applied — you saved ${formatCAD(data.discount_cents)}`);
      } else {
        setDiscount(null);
        toast.error(data.message || "Invalid code");
      }
    } catch (e) {
      logError("Promo validation failed", e);
      toast.error("Could not apply code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const validInfo =
    info.customer_name && /\S+@\S+\.\S+/.test(info.customer_email) &&
    (method === "pickup" || (info.line1 && info.city && info.province && info.postal_code));

  const fetchRates = async () => {
    setLoadingRates(true);
    setDeliveryError("");
    try {
      const { data } = await api.post("/delivery/quote", {
        shipping_address: {
          line1: info.line1, line2: info.line2, city: info.city,
          province: info.province, postal_code: info.postal_code, country: info.country,
        },
        subtotal_cents: subtotalCents,
      });
      if (!data.available) {
        setRates([]); setRateId(""); setQuoteInfo(null);
        setDeliveryError(data.reason || "Delivery is unavailable for this address.");
      } else {
        setRates(data.rates);
        setRateId(data.rates[0]?.id || "");
        setQuoteInfo({ distance_km: data.distance_km, duration_min: data.duration_min, rush: data.rush });
      }
    } catch (e) {
      logError("Delivery quote failed", e);
      setRates([]); setRateId(""); setQuoteInfo(null);
      setDeliveryError("We couldn't calculate a delivery fee right now. Please try again or choose in-store pickup.");
    } finally {
      setLoadingRates(false);
    }
  };

  const goToShipping = () => {
    if (!validInfo) return toast.error(method === "pickup" ? "Please add your name and email" : "Please complete your details");
    api.post("/cart/track", {
      email: info.customer_email,
      name: info.customer_name,
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    }).catch((e) => logError("Cart track failed", e));
    if (method === "pickup") {
      setRates([PICKUP_RATE]);
      setRateId("pickup");
      setStep(2);
    } else {
      setStep(1);
      fetchRates();
    }
  };

  const switchToPickup = () => {
    setMethod("pickup");
    setRates([PICKUP_RATE]);
    setRateId("pickup");
    setStep(2);
  };

  const placeOrder = async () => {
    if (!rateId) return toast.error("Select a delivery option");
    setPlacing(true);
    try {
      let paymentSourceId = null;
      let last4 = card.number.replace(/\s/g, "").slice(-4);
      if (SQUARE_ENABLED) {
        try {
          const tok = await squareRef.current.tokenize();
          paymentSourceId = tok.token;
          last4 = tok.last4 || last4;
        } catch (err) {
          logError("Square tokenize failed", err);
          toast.error(err.message || "Please check your card details");
          setPlacing(false);
          return;
        }
      }
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer_name: info.customer_name,
        customer_email: info.customer_email,
        customer_phone: info.customer_phone,
        shipping_address: method === "pickup" ? null : {
          line1: info.line1, line2: info.line2, city: info.city,
          province: info.province, postal_code: info.postal_code, country: info.country,
        },
        shipping_method_id: method === "pickup" ? "pickup" : rateId,
        pickup_slot: method === "pickup" ? (pickupSlot || "As soon as it's ready") : null,
        payment_source_id: paymentSourceId,
        card_last4: last4,
        discount_code: discount ? discount.code : null,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      toast.success("Order placed successfully!");
      navigate(`/order/${data.id}`, { state: { order: data, email: info.customer_email } });
    } catch (e) {
      logError("Checkout failed", e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setPlacing(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl px-5 py-12 lg:px-10">
      <h1 className="font-display text-5xl font-semibold tracking-tight text-forest lg:text-6xl">Checkout</h1>

      {/* Steps */}
      <div className="mt-8 flex items-center gap-2 sm:gap-4">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex flex-1 items-center gap-2 sm:gap-4">
            <div className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors ${step >= s.id ? "text-forest" : "text-muted-foreground"}`}>
              <span className={`flex h-8 w-8 items-center justify-center rounded-full border ${step > s.id ? "border-forest bg-forest text-white" : step === s.id ? "border-forest bg-leaf/10 text-forest" : "border-border"}`}>
                {step > s.id ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
              </span>
              <span className="hidden sm:inline">{s.id === 1 && method === "pickup" ? "Pickup" : s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 ${step > s.id ? "bg-forest" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <InformationStep
                method={method} setMethod={setMethod}
                info={info} setInfo={setInfo}
                pickupSlot={pickupSlot} setPickupSlot={setPickupSlot} pickupSlots={pickupSlots}
                onContinue={goToShipping}
              />
            )}
            {step === 1 && (
              <ShippingStep
                quoteInfo={quoteInfo} info={info}
                loadingRates={loadingRates} deliveryError={deliveryError}
                rates={rates} rateId={rateId} setRateId={setRateId}
                onSwitchToPickup={switchToPickup}
                onBack={() => setStep(0)} onContinue={() => setStep(2)}
              />
            )}
            {step === 2 && (
              <PaymentStep
                squareCfg={squareCfg} squareEnabled={SQUARE_ENABLED} squareRef={squareRef}
                card={card} setCard={setCard}
                placing={placing} totalCents={totalCents}
                onBack={() => setStep(method === "pickup" ? 0 : 1)}
                onPlaceOrder={placeOrder}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <OrderSummary
            items={items} subtotalCents={subtotalCents}
            discount={discount} discountCents={discountCents}
            selectedRate={selectedRate} shippingCents={shippingCents}
            taxCents={taxCents} totalCents={totalCents}
            promo={promo} setPromo={setPromo} applyPromo={applyPromo} applyingPromo={applyingPromo}
            onRemovePromo={() => { setDiscount(null); setPromo(""); }}
            customerEmail={info.customer_email}
          />
        </div>
      </div>
    </div>
  );
}
